import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Spinner,Pagination } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchActiveLeaves,
  fetchInactiveLeaves,
  updateLeave,
  cancelLeave,
  updateEditingLeave,
  clearEditingLeave,
  fetchUsersOnLeaveToday
} from './redux/UserAvailabilitySlicer';

const UserLeaveListModal = ({ show, onClose }) => {
  const dispatch = useDispatch();

  const { activeLeaves, inactiveLeaves, editingLeaves } = useSelector(
    (state) => state.himLeaveManagement
  );

  const [activeTab, setActiveTab] = useState('active');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(10);
  const [editingRow, setEditingRow] = useState(null);

  const isActive = activeTab === 'active';
  const current = isActive ? activeLeaves : inactiveLeaves;
  const { data, pagination, loading } = current;
  const { page, total_pages, total_records } = pagination;

  //Validations
  const formatDateForInput = (dateString) =>
    dateString ? dateString.substring(0, 10) : '';

  const getFieldValue = (leave, field) => {
    const leaveId = leave.user_leave_id;
    return editingLeaves[leaveId]?.[field] ?? leave[field];
  };

  const hasChanges = (leave) => {
    const originalFrom = formatDateForInput(leave.leave_from);
    const originalTo = formatDateForInput(leave.leave_to);
    const editedFrom = formatDateForInput(
      getFieldValue(leave, 'leave_from')
    );
    const editedTo = formatDateForInput(
      getFieldValue(leave, 'leave_to')
    );

    if (originalFrom === editedFrom && originalTo === editedTo) {
      toast.info('No Changes Done');
      return false;
    }
    return true;
  };

  const validateDates = (from, to) => {
    if (!from || !to) return 'Please select both leave dates';
    if (new Date(from) > new Date(to))
      return 'Leave from date cannot be after leave to date';
    return null;
  };

  
  //Initial Fetches
  useEffect(() => {
    if (show) {
      dispatch(fetchActiveLeaves({ page: 1, limit }));
      dispatch(fetchInactiveLeaves({ page: 1, limit }));
      setSearch('');
      setEditingRow(null);
    }
  }, [show, dispatch, limit]);

  //Search
  useEffect(() => {
  const timer = setTimeout(() => {
    const payload = { page: 1, limit, search };

    isActive
      ? dispatch(fetchActiveLeaves(payload))
      : dispatch(fetchInactiveLeaves(payload));
  }, 400);

  return () => clearTimeout(timer);
}, [search, activeTab, dispatch, limit]);

const handleCancelLeave = (leave) => {
    dispatch(cancelLeave(leave))
    .unwrap()
    .then((response) => {
    toast.success(response?.message || 'Leave cancelled successfully');
    dispatch(fetchActiveLeaves({ page, limit, search }));
    dispatch(fetchUsersOnLeaveToday({ page: 1, limit: 10 }));
  }).catch(err => {
    toast.error(err?.message || 'Error Cancelling Leave');
    console.log(err?.message || 'Error Canceling Leave')
  });
};


  //Pagination Function
  const changePage = (p) => {
  if (p < 1 || p > total_pages) return;

  const payload = { page: p, limit, search };

  isActive
    ? dispatch(fetchActiveLeaves(payload))
    : dispatch(fetchInactiveLeaves(payload));
};


  

  const handleSave = (leave) => {
    if (!hasChanges(leave)) return;

    const from = formatDateForInput(
      getFieldValue(leave, 'leave_from')
    );
    const to = formatDateForInput(
      getFieldValue(leave, 'leave_to')
    );

    const error = validateDates(from, to);
    if (error) {
      toast.error(error);
      return;
    }

    dispatch(
      updateLeave({
        user_leave_id: leave.user_leave_id,
        user_id: leave.user_id,
        username: leave.username,
        leave_from: from,
        leave_to: to
      })
    ).unwrap()
    .then((response) => {
      toast.success(response?.message || 'Leave updated successfully');
      setEditingRow(null);
      dispatch(clearEditingLeave({ leaveId: leave.user_leave_id }));
      dispatch(fetchActiveLeaves({ page, limit, search }));
      dispatch(fetchUsersOnLeaveToday({ page: 1, limit: 10 }));
    }).catch(err => {
      toast.error(err?.message || 'Failed to update leave');
      console.log(err?.message || 'Failed to update leave')
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Manage User Leaves</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Search*/}
        <div className="d-flex justify-content-between mb-3">
          <Form.Control
            style={{ width: 300 }}
            placeholder="Search by username"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/*Total Leaves*/}
          <div>
            Leaves: <strong>{total_records || 0}</strong>
          </div>
        </div>

        {/* Tabs */}
        <div className="ua-tabs-full mb-3">
          <div
            className={`ua-tab ${isActive ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('active');
              setSearch('');
            }}
          >
            Active Leaves
          </div>
          <div
            className={`ua-tab ${!isActive ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('inactive');
              setSearch('');
            }}
          >
            Leave History
          </div>
        </div>

        {/* Table */}
        <div style={{ height: 350, overflowY: 'auto' }}>
          <Table bordered size="sm">
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>From</th>
                <th>To</th>
                {isActive && <th>Actions</th>}
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={isActive ? 5 : 4}
                    className="text-center py-4"
                  >
                    <Spinner animation="border" />
                  </td>
                </tr>
              )}

              {!loading && data.length === 0 && (
                <tr>
                  <td
                    colSpan={isActive ? 5 : 4}
                    className="text-center py-3"
                  >
                    No {isActive ? 'active' : 'inactive'} leaves found
                  </td>
                </tr>
              )}

              {!loading &&
                data.map((leave) => {
                  const isEditing = editingRow === leave.user_leave_id;

                  return (
                    <tr key={leave.user_leave_id}>
                      <td>{leave.user_id}</td>
                      <td>{leave.username}</td>

                      <td>
                        <Form.Control
                          type="date"
                          size="sm"
                          disabled={!isEditing || !isActive}
                          value={formatDateForInput(
                            getFieldValue(leave, 'leave_from')
                          )}
                          onChange={(e) =>
                            dispatch(
                              updateEditingLeave({
                                leaveId: leave.user_leave_id,
                                field: 'leave_from',
                                value: e.target.value
                              })
                            )
                          }
                        />
                      </td>

                      <td>
                        <Form.Control
                          type="date"
                          size="sm"
                          disabled={!isEditing || !isActive}
                          value={formatDateForInput(
                            getFieldValue(leave, 'leave_to')
                          )}
                          onChange={(e) =>
                            dispatch(
                              updateEditingLeave({
                                leaveId: leave.user_leave_id,
                                field: 'leave_to',
                                value: e.target.value
                              })
                            )
                          }
                        />
                      </td>

                      {isActive && (
                        <td>
                          {isEditing ? (
                            <>
                              <Button
                                size="sm"
                                className="me-2"
                                onClick={() => handleSave(leave)}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  dispatch(
                                    clearEditingLeave({
                                      leaveId:
                                        leave.user_leave_id
                                    })
                                  );
                                  setEditingRow(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                className="me-2"
                                onClick={() =>
                                  setEditingRow(
                                    leave.user_leave_id
                                  )
                                }
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleCancelLeave(leave)}
                              >
                                Cancel Leave
                              </Button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </div>

        <div className="ua-leaves-pagination-footer">
          {/*Pagination Arrows*/}
          <Pagination className="ua-leaves-pagination">
            <Pagination.First
              className="ua-leaves-pagination-item"
              onClick={() => changePage(1)}
              disabled={page <= 1}
            />

            <Pagination.Prev
              className="ua-leaves-pagination-prev"
              onClick={() => changePage(page - 1)}
              disabled={page <= 1}
            />

            <Pagination.Item className="ua-leaves-pagination-item">
              Page {page} of {total_pages ? total_pages : 1}
            </Pagination.Item>

            <Pagination.Next
              className="ua-leaves-pagination-next"
              onClick={() => changePage(page + 1)}
              disabled={page >= total_pages}
            />

            <Pagination.Last
              className="ua-leaves-pagination-item"
              onClick={() => changePage(total_pages)}
              disabled={page >= total_pages}
            />
          </Pagination>

          {/* Rows per page*/}
          <div className="ua-leaves-rows-per-page">
            <Form.Select
              size="sm"
              className="ua-leaves-select"
              value={limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setLimit(newLimit);

                const payload = { page: 1, limit: newLimit, search };
                isActive
                  ? dispatch(fetchActiveLeaves(payload))
                  : dispatch(fetchInactiveLeaves(payload));
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Form.Select>
          </div>
        </div>

      </Modal.Body>
    </Modal>
  );
};

export default UserLeaveListModal;